import { resolveMediaUrl } from '@/lib/media';
import { useState, useRef } from 'react';
import { Upload, X, Camera, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { IMAGE_UPLOAD, validateUploadFile } from './FileDropzone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const ImageUpload = ({ value, onChange, label, shape = 'rect', className = '' }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const error = validateUploadFile(file, IMAGE_UPLOAD);
    if (error) {
      toast.error(error);
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = response.data.url;
      onChange(imageUrl);
      toast.success('Image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
    e.target.value = '';
  };

  const handleRemove = () => {
    onChange('');
  };

  const isCircle = shape === 'circle';

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_UPLOAD.accept}
        onChange={handleChange}
        className="hidden"
        data-testid={`upload-input-${label?.toLowerCase().replace(/\s/g, '-') || 'image'}`}
      />

      {value ? (
        <div className="relative group">
          <img
            src={resolveMediaUrl(value)}
            alt={label || 'Uploaded'}
            className={`object-cover border-2 border-[#27272A] ${
              isCircle ? 'w-24 h-24 rounded-full' : 'w-full h-32 rounded-xl'
            }`}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className={`absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity flex items-center justify-center ${
            isCircle ? 'rounded-full' : 'rounded-xl'
          }`}>
            <button
              type="button"
              data-testid={`change-${label?.toLowerCase().replace(/\s/g, '-') || 'image'}`}
              onClick={() => inputRef.current?.click()}
              aria-label={`Change ${label || 'image'}`}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            >
              <Camera className="w-4 h-4 text-white" />
            </button>
          </div>
          <button
            type="button"
            data-testid={`remove-${label?.toLowerCase().replace(/\s/g, '-') || 'image'}`}
            onClick={handleRemove}
            aria-label={`Remove ${label || 'image'}`}
            className="absolute right-2 top-2 z-10 p-2 rounded-full bg-black/60 hover:bg-red-500 transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`transition-all flex flex-col items-center justify-center gap-3 ${
            dragOver ? 'bg-violet-500/10' : ''
          } ${isCircle ? '' : 'w-full py-6'}`}
        >
          <button
            type="button"
            data-testid={`upload-${label?.toLowerCase().replace(/\s/g, '-') || 'image'}`}
            onClick={() => !uploading && inputRef.current?.click()}
            disabled={uploading}
            className={`flex items-center justify-center gap-2 font-medium transition-all ${
              isCircle
                ? 'w-24 h-24 rounded-full border-2 border-dashed border-[#27272A] hover:border-violet-400 bg-[#1E1E1E] hover:bg-violet-500/10'
                : 'px-5 py-2.5 rounded-full bg-violet-500 hover:bg-violet-600 text-white text-sm'
            }`}
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
            ) : isCircle ? (
              <Camera className="w-6 h-6 text-[#71717A]" />
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Add Image
              </>
            )}
          </button>
          {!isCircle && !uploading && (
            <span className="text-xs text-[#71717A]">or drag and drop</span>
          )}
        </div>
      )}
    </div>
  );
};

export { resolveMediaUrl as resolveImageUrl } from '@/lib/media';
export default ImageUpload;
